import { Commit } from 'vuex';
import { APP } from '@/api/com/MetaPattern/MDA/MD/Base/APP';
import { BaseElement } from '@/api/com/MetaPattern/MDA/MD/Base/BaseElement';
import { BEComponent } from '@/api/com/MetaPattern/MDA/MD/EntityModel/BEComponent';
import { Diagram } from '@/libs/com/MetaPattern/MDA/Designer/Repository/Diagram';
import { APPComponent } from '@/api/com/MetaPattern/MDA/MD/APPModel/APPComponent';
import { OutputInfo } from '@/libs/com/MetaPattern/MDA/Designer/Base/OutputInfo';
import { ValidationResult } from '@/api/com/MetaPattern/Core/Infrastructure/Common/ValidationResult';
import { CopyContent } from '@/libs/com/MetaPattern/Product/IDE/Util/CopyContent';
import { Dictionary } from '@/libs/com/MetaPattern/Product/IDE/Util/Dictionary';
import { Product } from '@/api/com/MetaPattern/MDA/MD/Base/Product';
import { ProductComponent } from '@/api/com/MetaPattern/MDA/MD/ProductModel/ProductComponent';
import { WorkScope } from '@/api/com/MetaPattern/MDA/MD/Base/WorkScope';
import { UIModelComponent } from '@/api/com/MetaPattern/MDA/MD/UIModel/UIModelComponent';
import { BaseComponent } from '@/api/com/MetaPattern/MDA/MD/Base/BaseComponent';

const state = {
  productCount: 0, // 当前用户所有Product的数量
  curCellId: '', // 当前画布上选中的图形元素Id(仅展示，未使用)
  curCell: null as any, // 当前画布上选中的图形元素
  curMDDesignId: '', // 当前操作的元数据的设计器ID：designId
  curMDElement: null as BaseElement, // 当前操作的元数据
  curDiagram: null as Diagram, // 当前操作的图形，只有具有图形设计的组件才设置值，否则为空
  curComponent: null as BaseComponent, // 当前操作的组件
  uiModelComponents: Array<UIModelComponent>(), // 所有当前打开的UI模型组件
  curLinkType: '', // 当前链接的命令
  workScopes: Array<WorkScope>(), // 当前用户所有的工作空间实例列表
  apps: Array<APP>(), // 所有的APP实例列表（用户的APP与系统的APP），包含了BEComponents
  products: Array<Product>(), // 当前用户所有的Product实例列表，包含了ProductComponent
  elements: Array<BaseElement>(), // 所有的元素，包括所有APP下的所有子元素，用于全局根据designId查找元数据   TODO：是否因为存储过多引起性能问题
  diagrams: Array<Diagram>(), // 所有当前打开的图表
  validationErrors: Array<ValidationResult>(), // 模型校验的错误消息
  outputMessages: Array<OutputInfo>(), // 输出信息
  copyContents: Array<CopyContent>(), // 复制的内容列表
  componentElements: new Dictionary<string, Array<string>>() // <组件设计期ID, Array<元素ID>>
};

const getters = {};

const setComponentElement = (componentDesignId: string, elementDesignId: string): void => {
  let elementDesignIds: Array<string>;
  if (!state.componentElements.contains(componentDesignId)) {
    elementDesignIds = new Array<string>();
    state.componentElements.append(componentDesignId, elementDesignIds);
  } else {
    elementDesignIds = state.componentElements.getValue(componentDesignId);
  }
  if (elementDesignIds.indexOf(elementDesignId) < 0) {
    elementDesignIds.push(elementDesignId);
  }
};

const pushElement = (componentDesignId: string, element: BaseElement) => {
  state.elements.push(element);
  if (componentDesignId) {
    setComponentElement(componentDesignId, element.designId);
  }
};

const setDiagramElements = (componentDesignId: string, elements: Array<BaseElement>, handleIds: Array<string>) => {
  elements.forEach((element: BaseElement) => {
    const find = state.elements.find((item: BaseElement) => item.designId === element.designId);
    if (!find) {
      pushElement(componentDesignId, element);
    } else {
      find.copyFrom(element, true);
    }
    handleIds.push(element.designId);
  });
};

const mutations = {
  clear(states: any) {
    state.productCount = 0;
    state.curCellId = '';
    state.curCell = null as any;
    state.curMDDesignId = '';
    state.curMDElement = null as BaseElement;
    state.curDiagram = null as Diagram;
    state.curComponent = null as BaseComponent;
    state.uiModelComponents = Array<UIModelComponent>();
    state.curLinkType = '';
    state.workScopes = Array<WorkScope>();
    state.apps = Array<APP>();
    state.products = Array<Product>();
    state.elements = Array<BaseElement>();
    state.diagrams = Array<Diagram>();
    state.validationErrors = Array<ValidationResult>();
    state.outputMessages = Array<OutputInfo>();
    state.copyContents = Array<CopyContent>();
    state.componentElements = new Dictionary<string, Array<string>>();
  },
  setProducts(states: any, params: Array<Product>) {
    if (params && params.length > 0) {
      states.products = params;
      states.products.forEach((element: Product) => {
        if (!states.elements.find((item: BaseElement) => item.designId === element.designId)) {
          pushElement(element.designId, element);
        }
        element.productComponents.forEach((com: ProductComponent) => {
          if (!states.elements.find((item: BaseElement) => item.designId === com.designId)) {
            pushElement(com.designId, com);
          }
        });
      });
      states.productCount = states.products.length;
    }
  },
  setWorkScopes(states: any, params: Array<WorkScope>) {
    if (params && params.length > 0) {
      states.workScopes = params;
    }
  },
  setApps(states: any, params: Array<APP>) {
    if (params && params.length > 0) {
      params.forEach(param => {
        if (!states.apps.find((app: APP) => app.designId === param.designId)) {
          states.apps.push(param);
        }
      });
      states.apps.forEach((element: APP) => {
        if (!states.elements.find((item: BaseElement) => item.designId === element.designId)) {
          pushElement(element.designId, element);
        }
        element.bEComponents.forEach((com: BEComponent) => {
          if (!states.elements.find((item: BaseElement) => item.designId === com.designId)) {
            pushElement(com.designId, com);
          }
        });
        element.aPPComponents.forEach((com: APPComponent) => {
          if (!states.elements.find((item: BaseElement) => item.designId === com.designId)) {
            pushElement(com.designId, com);
          }
        });
        element.uIModelComponents.forEach((com: UIModelComponent) => {
          if (!states.elements.find((item: BaseElement) => item.designId === com.designId)) {
            pushElement(com.designId, com);
          }
        });
      });
    }
  },
  // 如果不存在则加入元数据，否则复制元数据信息
  setElement(states: any, params: any) {
    if (params) {
      const element = states.elements.find((item: BaseElement) => item.designId === params.element.designId) as BaseElement;
      if (element) {
        element.copyFrom(params.element, false);
      } else {
        pushElement(params.componentDesignId, params.element);
      }
    }
  },
  removeElement(states: any, params: any) {
    if (params && params !== '') {
      states.elements = states.elements.filter((element: BaseElement) => element.designId !== params.element.designId);
      const componentContainElementds = state.componentElements.getValue(params.componentDesignId);
      if (componentContainElementds) {
        componentContainElementds.splice(componentContainElementds.findIndex(item => item === params.element.designId), 1);
      }
    }
  },
  setUIModelComponent(states: any, params: UIModelComponent) {
    if (params) {
      const findComponent = states.uiModelComponents.find((item: UIModelComponent) => item.designId === params.designId);
      if (!findComponent) {
        states.uiModelComponents.push(params);
      }
    }
  },
  setDiagram(states: any, params: Diagram) {
    if (params) {
      const handleIds = new Array<string>();
      const removeElements = new Array<BaseElement>();
      const findDiagram = states.diagrams.find((item: Diagram) => item.component.designId === params.component.designId);
      if (!findDiagram) {
        states.diagrams.push(params);
      }
      handleIds.push(params.component.designId);
      if (params.component instanceof BEComponent) {
        const bEComponent = params.component as BEComponent;
        setDiagramElements(bEComponent.designId, bEComponent.comments, handleIds);
        setDiagramElements(bEComponent.designId, bEComponent.entities, handleIds);
        bEComponent.entities.forEach((entity) => {
          // TODO : 跨应用在这里赋值
          if (entity.superEntity_ID !== null && entity.superEntity === null) {
            entity.superEntity = states.elements.find((element: BaseElement) => element.id === entity.superEntity_ID);
          }
          setDiagramElements(bEComponent.designId, entity.attributes, handleIds);
          setDiagramElements(bEComponent.designId, entity.methods, handleIds);
          entity.methods.forEach((method) => {
            setDiagramElements(bEComponent.designId, method.parameters, handleIds);
          });
        });
        setDiagramElements(bEComponent.designId, bEComponent.interfaces, handleIds);
        bEComponent.interfaces.forEach((beInterface) => {
          setDiagramElements(bEComponent.designId, beInterface.attributes, handleIds);
          setDiagramElements(bEComponent.designId, beInterface.methods, handleIds);
          beInterface.methods.forEach((method) => {
            setDiagramElements(bEComponent.designId, method.parameters, handleIds);
          });
        });
        setDiagramElements(bEComponent.designId, bEComponent.enums, handleIds);
        bEComponent.enums.forEach((bEEnum) => {
          setDiagramElements(bEComponent.designId, bEEnum.enumItems, handleIds);
        });
        setDiagramElements(bEComponent.designId, bEComponent.services, handleIds);
        bEComponent.services.forEach((service) => {
          setDiagramElements(bEComponent.designId, service.serviceActions, handleIds);
        });
        setDiagramElements(bEComponent.designId, bEComponent.externalServices, handleIds);
        bEComponent.externalServices.forEach((externalService) => {
          setDiagramElements(bEComponent.designId, externalService.parameters, handleIds);
        });
        setDiagramElements(bEComponent.designId, bEComponent.businessLogics, handleIds);
        bEComponent.businessLogics.forEach((businessLogic) => {
          setDiagramElements(bEComponent.designId, businessLogic.parameters, handleIds);
        });
        setDiagramElements(bEComponent.designId, bEComponent.dTOes, handleIds);
        bEComponent.dTOes.forEach((dto) => {
          setDiagramElements(bEComponent.designId, dto.attributes, handleIds);
        });
        setDiagramElements(bEComponent.designId, bEComponent.valueObjects, handleIds);
        bEComponent.valueObjects.forEach((valueObject) => {
          setDiagramElements(bEComponent.designId, valueObject.attributes, handleIds);
          setDiagramElements(bEComponent.designId, valueObject.methods, handleIds);
          valueObject.methods.forEach((method) => {
            setDiagramElements(bEComponent.designId, method.parameters, handleIds);
          });
        });
        setDiagramElements(bEComponent.designId, bEComponent.constants, handleIds);
        bEComponent.constants.forEach((constant) => {
          setDiagramElements(bEComponent.designId, constant.items, handleIds);
        });
        setDiagramElements(bEComponent.designId, bEComponent.referenceEntities, handleIds);
        setDiagramElements(bEComponent.designId, bEComponent.distributedTransactions, handleIds);
        bEComponent.distributedTransactions.forEach((distributedTransaction) => {
          setDiagramElements(bEComponent.designId, distributedTransaction.attributes, handleIds);
        });
        setDiagramElements(bEComponent.designId, bEComponent.entityGenerations, handleIds);
        setDiagramElements(bEComponent.designId, bEComponent.interfaceGenerations, handleIds);
        setDiagramElements(bEComponent.designId, bEComponent.implements, handleIds);
        setDiagramElements(bEComponent.designId, bEComponent.associations, handleIds);
        setDiagramElements(bEComponent.designId, bEComponent.dependencyAssociations, handleIds);
        setDiagramElements(bEComponent.designId, bEComponent.compositions, handleIds);
        setDiagramElements(bEComponent.designId, bEComponent.aggregations, handleIds);
        setDiagramElements(bEComponent.designId, bEComponent.crossAssociations, handleIds);
      } else if (params.component instanceof APPComponent) {
        const appComponent = params.component as APPComponent;
        setDiagramElements(appComponent.designId, appComponent.comments, handleIds);
        setDiagramElements(appComponent.designId, appComponent.menuSets, handleIds);
        appComponent.menuSets.forEach((menuset) => {
          setDiagramElements(appComponent.designId, menuset.menuItems, handleIds);
        });
        setDiagramElements(appComponent.designId, appComponent.configGroups, handleIds);
        appComponent.configGroups.forEach((configGroup) => {
          setDiagramElements(appComponent.designId, configGroup.configItems, handleIds);
        });
        setDiagramElements(appComponent.designId, appComponent.menuContains, handleIds);
      } else if (params.component instanceof ProductComponent) {
        const productComponent = params.component as ProductComponent;
        setDiagramElements(productComponent.designId, productComponent.comments, handleIds);
        setDiagramElements(productComponent.designId, productComponent.productAPPs, handleIds);
      }
      const componentContainElements = states.componentElements.getValue(params.component.designId);
      if (componentContainElements) {
        componentContainElements.forEach((elementDesignId: string) => {
          if (handleIds.indexOf(elementDesignId) < 0) {
            const removeElement = states.elements.find((item: BaseElement) => item.designId === elementDesignId);
            if (removeElement) {
              removeElements.push(removeElement);
            }
          }
        });
        removeElements.forEach(element => {
          states.elements.splice(states.elements.indexOf(element), 1);
        });
      }
    }
  },
  setCurMDDesignId(states: any, params: string) {
    states.curMDDesignId = params;
    const components: Array<BaseElement> = states.elements as Array<BaseElement>;
    components.forEach((item: BaseElement) => {
      if (item.designId === states.curMDDesignId) {
        states.curMDElement = item;
      }
    });
  },
  setCurCellId(states: any, params: string) {
    states.curCellId = params;
    if (params === '') {
      states.curCell = null;
    }
  },
  setCurCell(states: any, params: any) {
    states.curCell = params;
    if (params != null) {
      states.curCellId = params.id;
    } else {
      states.curCellId = '';
    }
  },
  setCurMDElement(states: any, params: any) {
    if (params && params != null) {
      states.curMDElement = params.element;
      states.curMDDesignId = params.element.designId;
      const find = states.elements.find((item: BaseElement) => item.designId === params.element.designId);
      if (!find) {
        pushElement(params.componentDesignId, params.element);
      }
    } else {
      states.curMDElement = null;
      states.curMDDesignId = null;
    }
  },
  // 设置当前链接类型
  setCurLinkType(states: any, params: string) {
    if (params && params != null) {
      states.curLinkType = params;
    } else {
      states.curLinkType = '';
    }
  },
  // 设置模型校验的结果
  setValidationErrors(states: any, params: Array<ValidationResult>) {
    if (params && params != null) {
      states.validationErrors = params;
    } else {
      states.validationErrors = new Array<ValidationResult>();
    }
  },
  // 设置输出消息
  setOutputMessages(states: any, params: string) {
    if (states.outputMessages == null) {
      states.outputMessages = new Array<OutputInfo>();
    }
    while (states.outputMessages.length >= 50) {
      states.outputMessages.shift();
    }
    states.outputMessages.push(OutputInfo.create(params));
  },
  // 设置输出消息
  setCopyContents(states: any, params: Array<CopyContent>) {
    if (params && params != null) {
      states.copyContents = params;
    } else {
      states.copyContents = new Array<CopyContent>();
    }
  },
  // 设置当前的图形，可以设置为null，代表没有任何图形
  // 元素和关系，与图形同步
  // 属性每次设置的时候进行同步，以保证图形fromGragh的时候的一致性
  setCurDiagram(states: any, params: Diagram | null) {
    if (params && params !== null) {
      states.curDiagram = params;
      states.curComponent = params.component;
    } else {
      states.curDiagram = null;
      states.curComponent = null;
    }
  },
  // 设置当前文档的组件，可以设置为null，代表没有任何文档
  setCurComponent(states: any, params: BaseComponent | null) {
    if (params) {
      states.curComponent = params;
    } else {
      states.curComponent = null;
    }
  }
};

const actions = {
  clearAction(context: { commit: Commit }) {
    context.commit('clear');
  },
  setProductsAction(context: { commit: Commit }, params: Array<Product>) {
    context.commit('setProducts', params);
  },
  setWorkScopesAction(context: { commit: Commit }, params: Array<WorkScope>) {
    context.commit('setWorkScopes', params);
  },
  setAppsAction(context: { commit: Commit }, params: Array<APP>) {
    context.commit('setApps', params);
  },
  setCurMDDesignIdAction(context: { commit: Commit }, params: string) {
    context.commit('setCurMDDesignId', params);
  },
  setCurCellIdAction(context: { commit: Commit }, params: string) {
    context.commit('setCurCellId', params);
  },
  setCurCellAction(context: { commit: Commit }, params: any) {
    context.commit('setCurCell', params);
  },
  setUIModelComponentAction(context: { commit: Commit }, params: UIModelComponent) {
    context.commit('setUIModelComponent', params);
  },
  setDiagramAction(context: { commit: Commit }, params: Diagram) {
    context.commit('setDiagram', params);
  },
  setElementAction(context: { commit: Commit }, params: any) {
    context.commit('setElement', params);
  },
  removeElementAction(context: { commit: Commit }, params: any) {
    context.commit('removeElement', params);
  },
  setCurMDElementAction(context: { commit: Commit }, params: any) {
    context.commit('setCurMDElement', params);
  },
  setCurDiagramAction(context: { commit: Commit }, params: Diagram | null) {
    context.commit('setCurDiagram', params);
  },
  setCurComponentAction(context: { commit: Commit }, params: BaseComponent | null) {
    context.commit('setCurComponent', params);
  },
  setCurLinkTypeAction(context: { commit: Commit }, params: string) {
    context.commit('setCurLinkType', params);
  },
  setValidationErrorsAction(context: { commit: Commit }, params: Array<ValidationResult>) {
    context.commit('setValidationErrors', params);
  },
  setOutputMessagesAction(context: { commit: Commit }, params: Array<OutputInfo>) {
    context.commit('setOutputMessages', params);
  },
  setCopyContentsAction(context: { commit: Commit }, params: Array<CopyContent>) {
    context.commit('setCopyContents', params);
  }
};

export default {
  namespaced: true, // namespaced为false的时候，state,mutations,actions全局可以调用，为true，生成作用域，引用时要声明模块名称
  state,
  getters,
  mutations,
  actions
};
